import { Box, Button, Container, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import Loading from './Loading';
import '../components/Style.css';
import { useSaveBlogDetailMutation, useUpdateBlogDetailMutation } from '../store/api/blogsApi';
import { useUploadImageMutation } from '../store/api/parkingTypeApi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EditorToolbar, { modules, formats } from './EditorToolbar';

type Props = {};

const BlogSchema = Yup.object().shape({
  description: Yup.string().required('Please enter description').trim(),
  title: Yup.string().required('Please enter blog title').trim(),
});

const ResponsiveBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const FormikBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
  [theme.breakpoints.up('md')]: {
    marginLeft: '20px',
  },
}));

const config: any = {
  toolbarAdaptive: false,
};

const AddBlog = (props: Props) => {
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    description: '',
    title: '',
    status: true,
  });
  const [isImgUrl, setIsImgUrl] = useState(false);
  const { state }: any = useLocation();
  const token = JSON.parse(localStorage.getItem('login') || '{}').token;
  const userId = JSON.parse(localStorage.getItem('login') || '{}').userId;
  const [saveBlogDetail, addBlogDataResponse] = useSaveBlogDetailMutation();
  const [updateBlogDetail, updateBlogDataResponse] = useUpdateBlogDetailMutation();
  const [uploadBlogImage, uploadBlogImageResponse] = useUploadImageMutation();
  const [submitData, setSubmitData] = useState<any | null>();
  const blogImageInput: any = useRef();
  const [blogImage, setBlogImage] = useState<null | any>();

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.description || values.description == '<p><br></p>') errors['description'] = 'This field is required';
    return errors;
  };

  const handleAddOrUpdateBlog = (values: any, action: any) => {
    if (!state.editMode) {
      setSubmitData({
        description: values.description,
        title: values?.title,
        imagePath: blogImage,
        status: values.status,
        userId: userId,
      });
      const formData = new FormData();
      formData.append('file', blogImage);
      uploadBlogImage({
        token: token,
        payload: formData,
      });
    } else {
      setSubmitData({
        id: state.editData.id,
        description: values.description,
        title: values?.title,
        imagePath: blogImage,
        status: values.status,
        userId: userId,
      });
      if (!isImgUrl) {
        const formData = new FormData();
        formData.append('file', blogImage);
        uploadBlogImage({
          token: token,
          payload: formData,
        });
      } else {
        updateBlogDetail({
          token: token,
          payload: {
            id: state.editData.id,
            description: values.description,
            title: values?.title,
            status: values.status,
            imagePath: blogImage.split('?')[0].split('com/')[1],
            userId: userId,
          },
        });
      }
    }
  };

  useEffect(() => {
    if (state.editData) {
      setInitialValues({
        title: state.editData.title,
        description: state.editData.description,
        status: state.editData.status,
      });
      setBlogImage(state.editData.imageUrl);
      setIsImgUrl(true);
    }
  }, []);

  {
    /* add blog*/
  }
  useEffect(() => {
    if (uploadBlogImageResponse.isSuccess && !state.editMode) {
      saveBlogDetail({
        token: token,
        payload: { ...submitData, imagePath: uploadBlogImageResponse.data.imagePath },
      });
    } else if (uploadBlogImageResponse.isSuccess && !isImgUrl) {
      updateBlogDetail({
        token: token,
        payload: { ...submitData, imagePath: uploadBlogImageResponse.data.imagePath },
      });
    } else if (uploadBlogImageResponse.isError) {
      toast.error('Image size is too large');
    }
  }, [uploadBlogImageResponse.isSuccess, uploadBlogImageResponse.isError]);

  useEffect(() => {
    if (addBlogDataResponse.isSuccess) {
      toast.success('Blog added successfully');
      navigate('/dashboard/blogs', { replace: true });
    } else if (addBlogDataResponse.isError) {
      let errorData: any = addBlogDataResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }

    if (updateBlogDataResponse.isSuccess) {
      toast.success('Blog updated successfully');
      navigate('/dashboard/blogs', { replace: true });
    } else if (updateBlogDataResponse.isError) {
      let errorData: any = updateBlogDataResponse.error;
      let msg: any = errorData?.data?.serviceErrors[0]?.msg;
      toast.error('Something went wrong ' + msg || '');
    }
  }, [
    addBlogDataResponse.isSuccess,
    addBlogDataResponse.isError,
    updateBlogDataResponse.isSuccess,
    updateBlogDataResponse.isError,
  ]);

  return (
    <Box>
      <Container>
        <h1>{state.editMode ? 'Edit Blog' : 'Add Blog'}</h1>
        <ResponsiveBox style={{ display: 'flex', paddingTop: '40px' }}>
          <Box>
            <input
              type="file"
              name="blog-image"
              id="blog-image"
              ref={blogImageInput}
              accept="image/gif, image/jpeg, image/png"
              onChange={(event: any) => {
                if (state.editMode && isImgUrl) {
                  setIsImgUrl(false);
                }
                setBlogImage(event.target.files[0]);
              }}
              hidden
            />
            <Box
              onClick={() => {
                blogImageInput.current.click();
              }}
            >
              {blogImage && (
                <Box style={{ position: 'relative' }}>
                  <img
                    src={state.editMode && isImgUrl ? blogImage : URL.createObjectURL(blogImage)}
                    alt=""
                    height={200}
                    width={200}
                    style={{ borderRadius: '100px' }}
                  />
                  <Box
                    style={{
                      background: 'white ',
                      padding: '3px',
                      borderRadius: '100px',
                      position: 'absolute',
                      bottom: '5px',
                      right: '30px',
                      fontSize: '34px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '40px',
                      width: '40px',
                    }}
                  >
                    <AddPhotoAlternateIcon />
                  </Box>
                </Box>
              )}

              <div
                style={{
                  position: 'absolute',
                  top: '5px',
                  right: '10px',
                  fontSize: '27px',
                  backgroundColor: 'white',
                  width: '26px',
                  height: '32px',
                  borderRadius: '50%',
                }}
              ></div>
              {!blogImage && (
                <Box
                  style={{
                    background: 'lightgray',
                    height: 200,
                    width: 200,
                    borderRadius: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <h5>Upload photo</h5>
                </Box>
              )}
            </Box>
            <Box style={{ textAlign: 'center', marginTop: '10px' }}>
              <p>Allowed *.jpeg, *.jpg, *.png, *.gif</p>
              <p> Max size of 3.1 MB</p>
            </Box>
          </Box>
          <FormikBox style={{ flex: 1, padding: 0 }}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={handleAddOrUpdateBlog}
              validationSchema={BlogSchema}
              validate={validate}
            >
              {({ handleBlur, handleChange, handleSubmit, values, touched, errors, setFieldValue }) => (
                <Box>
                  <Box style={{ width: '100%' }}>
                    <Typography variant="h5" marginTop={2} gutterBottom component="div">
                      Title
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="title"
                      label="Enter blog title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.title && errors.title)}
                      helperText={touched.title && errors.title}
                    />
                  </Box>

                  <Box style={{ width: '100%' }}>
                    <Typography variant="h5" marginTop={2} gutterBottom component="div">
                      Description
                    </Typography>
                    <EditorToolbar toolbarId={'t1'} />
                    <ReactQuill
                      theme="snow"
                      onChange={(newContent: any) => {
                        setFieldValue('description', newContent);
                      }}
                      value={values.description}
                      modules={modules('t1')}
                      formats={formats}
                    />
                    {errors.description && <div style={{ color: 'red' }}>{errors.description}</div>}
                  </Box>
                  <Box style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                      variant="contained"
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      onClick={() => {
                        navigate('/dashboard/blogs');
                      }}
                      sx={{ marginRight: '10px' }}
                      size="large"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      style={{ color: 'white', backgroundColor: '#E23744' }}
                      size="large"
                      component="span"
                      onClick={() => {
                        if (!blogImage) {
                          toast.error('Please upload an image');
                        } else {
                          handleSubmit();
                        }
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              )}
            </Formik>
          </FormikBox>
        </ResponsiveBox>
      </Container>
      {addBlogDataResponse.isLoading || updateBlogDataResponse.isLoading || uploadBlogImageResponse.isLoading ? (
        <Loading />
      ) : null}
    </Box>
  );
};

export default AddBlog;
